import { useVoiceAssistant } from '../../contexts/VoiceAssistantContext';
import { X, Mic, MicOff, Sparkles, User, Loader2, Volume2, Minus, AlertTriangle } from 'lucide-react';
import { useState, useEffect } from 'react';

export default function FloatingVoiceAssistant() {
    const {
        isOpen,
        isMinimized,
        state,
        chatHistory,
        errorMessage,
        open,
        close,
        minimize,
        expand,
        startListening,
        stopListening,
        isRecording,
        volumeLevel,
    } = useVoiceAssistant();

    const [showMicWarning, setShowMicWarning] = useState(false);

    // Mic Blocked Check
    useEffect(() => {
        let checkTimer: NodeJS.Timeout;
        if (state === 'listening' && isRecording) {
            // If volume stays 0 for 3 seconds while listening, warn user
            const startCheck = Date.now();
            checkTimer = setInterval(() => {
                if (Date.now() - startCheck > 3000 && volumeLevel === 0) {
                    setShowMicWarning(true);
                } else if (volumeLevel > 0.05) {
                    setShowMicWarning(false);
                }
            }, 1000);
        } else {
            setShowMicWarning(false);
        }
        return () => clearInterval(checkTimer);
    }, [state, isRecording, volumeLevel]);

    // Not opened yet - show floating button
    if (!isOpen) {
        return (
            <button
                onClick={open}
                className="fixed bottom-20 right-4 w-14 h-14 rounded-full bg-[#0D5F6B] shadow-lg flex items-center justify-center z-50 hover:bg-[#0a4a54] transition-colors"
            >
                <Sparkles className="w-6 h-6 text-white" />
            </button>
        );
    }

    // Minimized - show small status button
    if (isMinimized) {
        return (
            <button
                onClick={expand}
                className={`fixed bottom-20 right-4 w-14 h-14 rounded-full shadow-lg flex items-center justify-center z-50 transition-colors ${state === 'listening' ? 'bg-red-500 animate-pulse' :
                    state === 'speaking' ? 'bg-[#0D5F6B] animate-pulse' :
                        state === 'processing' ? 'bg-gray-500' :
                            'bg-[#0D5F6B]'
                    }`}
            >
                {state === 'listening' ? (
                    <MicOff className="w-6 h-6 text-white" />
                ) : state === 'speaking' ? (
                    <Volume2 className="w-6 h-6 text-white" />
                ) : state === 'processing' ? (
                    <Loader2 className="w-6 h-6 text-white animate-spin" />
                ) : (
                    <Sparkles className="w-6 h-6 text-white" />
                )}
            </button>
        );
    }

    // Expanded - show full panel
    const getMicColor = () => {
        if (state === 'listening') return 'bg-red-500';
        if (state === 'processing' || state === 'speaking') return 'bg-gray-400';
        if (state === 'error') return 'bg-red-500';
        return 'bg-[#0D5F6B]';
    };

    const getStateText = () => {
        switch (state) {
            case 'idle': return 'Initializing...';
            case 'connecting': return 'Connecting...';
            case 'ready': return 'Ready';
            case 'listening': return 'Listening...';
            case 'processing': return 'Processing...';
            case 'speaking': return 'Speaking...';
            case 'error': return 'Error';
        }
    };

    return (
        <div className="fixed bottom-20 right-4 w-80 h-[480px] bg-white rounded-2xl shadow-2xl z-50 flex flex-col overflow-hidden border border-gray-200">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
                <div className="flex items-center gap-2">
                    {state === 'speaking' ? (
                        <Volume2 className="w-4 h-4 text-[#0D5F6B] animate-pulse" />
                    ) : (
                        <Sparkles className="w-4 h-4 text-[#0D5F6B]" />
                    )}
                    <span className="text-sm font-medium text-gray-700">{getStateText()}</span>
                </div>
                <div className="flex items-center gap-1">
                    <button
                        onClick={minimize}
                        className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-200 transition-colors"
                    >
                        <Minus className="w-4 h-4 text-gray-500" />
                    </button>
                    <button
                        onClick={close}
                        className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-200 transition-colors"
                    >
                        <X className="w-4 h-4 text-gray-500" />
                    </button>
                </div>
            </div>

            {/* Chat Area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {chatHistory.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-center">
                        <Sparkles className="w-12 h-12 text-[#0D5F6B] mb-3" />
                        <h3 className="text-base font-semibold text-gray-900 mb-1">AI Assistant</h3>
                        <p className="text-xs text-gray-500 max-w-[200px]">
                            Tap the mic to speak. I can help with tasks, calendar, and more!
                        </p>
                    </div>
                ) : (
                    chatHistory.map((msg, i) => (
                        <div key={i} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                            {msg.role === 'assistant' && (
                                <div className="w-6 h-6 rounded-full bg-[#0D5F6B] flex items-center justify-center flex-shrink-0">
                                    <Sparkles className="w-3 h-3 text-white" />
                                </div>
                            )}
                            <div className={`max-w-[80%] rounded-xl px-3 py-2 ${msg.role === 'user' ? 'bg-[#0D5F6B] text-white' : 'bg-gray-100'
                                }`}>
                                <p className={`text-xs ${msg.role === 'user' ? 'text-white' : 'text-gray-900'}`}>
                                    {msg.text}
                                </p>
                                {msg.actions && msg.actions.length > 0 && (
                                    <div className="mt-1 space-y-0.5">
                                        {msg.actions.map((action, j) => (
                                            <p key={j} className="text-xs text-green-600 font-medium">{action}</p>
                                        ))}
                                    </div>
                                )}
                            </div>
                            {msg.role === 'user' && (
                                <div className="w-6 h-6 rounded-full bg-gray-400 flex items-center justify-center flex-shrink-0">
                                    <User className="w-3 h-3 text-white" />
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>

            {/* Error */}
            {errorMessage && (
                <div className="px-4 pb-2">
                    <div className="bg-red-100 rounded-lg p-2">
                        <p className="text-xs text-red-600 text-center">{errorMessage}</p>
                    </div>
                </div>
            )}

            {/* Mic Button */}
            <div className="flex flex-col items-center pb-4 pt-2 relative">
                {/* Volume Ring Visualization */}
                {state === 'listening' && (
                    <div
                        className="absolute top-2 left-1/2 -translate-x-1/2 w-12 h-12 rounded-full border-2 border-red-500/50 transition-all duration-75"
                        style={{
                            transform: `translateX(-50%) scale(${1 + volumeLevel * 1.5})`,
                            opacity: 0.5 + volumeLevel * 0.5
                        }}
                    ></div>
                )}

                <button
                    onClick={isRecording ? stopListening : startListening}
                    disabled={state === 'connecting' || state === 'processing' || state === 'idle'}
                    className={`relative z-10 w-12 h-12 rounded-full flex items-center justify-center transition-all shadow-lg ${getMicColor()} disabled:opacity-50`}
                >
                    {state === 'connecting' || state === 'idle' ? (
                        <Loader2 className="w-5 h-5 text-white animate-spin" />
                    ) : state === 'listening' ? (
                        <MicOff className="w-5 h-5 text-white" />
                    ) : state === 'speaking' ? (
                        <Volume2 className="w-5 h-5 text-white animate-pulse" />
                    ) : state === 'processing' ? (
                        <Loader2 className="w-5 h-5 text-white animate-spin" />
                    ) : (
                        <Mic className="w-5 h-5 text-white" />
                    )}
                </button>

                {/* Mic Blocked Warning */}
                {showMicWarning && (
                    <div className="absolute -top-10 bg-red-100 text-red-600 px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 animate-bounce">
                        <AlertTriangle className="w-3 h-3" />
                        <span>No audio detected</span>
                    </div>
                )}

                <p className="text-xs text-gray-400 mt-1">
                    {state === 'listening' ? 'Tap to stop' : state === 'ready' ? 'Tap to speak' : ''}
                </p>
            </div>
        </div>
    );
}
